class PdpRedesign {
  constructor(root) {
    this.root = root;
    this.galleryTrack = root.querySelector('[data-pdp-gallery-track]');
    this.thumbs = [...root.querySelectorAll('[data-pdp-gallery-thumb]')];
    this.desktopItems = [...root.querySelectorAll('[data-pdp-gallery-desktop-item]')];
    this.configureCards = [...root.querySelectorAll('[data-pdp-configure-card]')];
    this.videoModal = root.querySelector('[data-pdp-video-modal]');
    this.videoMedia = root.querySelector('[data-pdp-video-media]');
    this.videoProductCard = root.querySelector('[data-pdp-video-product-card]');
    this.compareModal = root.querySelector('[data-pdp-compare-modal]');
    this.reviewsModal = root.querySelector('[data-pdp-reviews-modal]');
    this.bnplModals = [...root.querySelectorAll('[data-pdp-bnpl-modal]')];
    this.productForm = root.querySelector('form[id^="product-form"]');
    this.variantInput = root.querySelector('[data-pdp-variant-input]');
    this.ctaMode = root.classList.contains('pdp-redesign--preorder') ? 'preorder' : 'buy';
    this.selectedAddons = [];
    this.videoTriggers = [...root.querySelectorAll('[data-pdp-video-trigger]')];
    this.variants = this.parseJson('[data-pdp-variants-json]') || [];
    this.variantMediaMap = this.parseJson('[data-pdp-variant-media-json]') || {};
    this.variantPreorderMap = this.parseJson('[data-pdp-variant-preorder-json]') || {};
    this.updateUrl = root.dataset.updateUrl === 'true';

    // Run each binder in isolation: if one throws (e.g. a malformed metafield
    // or a missing element on an atypical product), the others still attach.
    // Previously an early failure would abort the whole constructor and leave
    // later features — including the how-to video modal — wired to nothing.
    [
      'bindSectionZoom',
      'bindGallery',
      'bindGalleryVideos',
      'bindStandaloneAutoplayVideos',
      'bindGalleryZoom',
      'bindConfigure',
      'bindVideoModal',
      'bindHowToHover',
      'bindHowToLoop',
      'bindCompareModal',
      'bindReviewsModal',
      'bindBnplModal',
      'bindCmpSliders',
      'bindVariantPicker',
      'bindProductOptions',
      'bindVariantGallery',
      'bindBundleStrip',
      'bindCartSubmit',
      'bindGalleryPaneScroll',
      'bindSizeChartModal',
      'bindBedsheetQuiz',
      'bindStickyAtcScroll',
      'bindSectionReveal',
    ].forEach((method) => {
      try {
        this[method]();
      } catch (error) {
        console.error(`PdpRedesign: ${method} failed`, error);
      }
    });

    this.updatePaymentTerms();
  }

  parseJson(selector) {
    const node = this.root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  isModalOpen(modal) {
    return Boolean(modal && !modal.hidden);
  }

  getSizeChartModal() {
    return this.sizeChartModal
      || this.root.querySelector('[data-pdp-size-chart-modal]')
      || document.querySelector('[data-pdp-size-chart-modal]');
  }

  syncBodyScrollLock() {
    const locked = this.isModalOpen(this.zoomOverlay)
      || this.isModalOpen(this.videoModal)
      || this.isModalOpen(this.compareModal)
      || this.isModalOpen(this.reviewsModal)
      || this.bnplModals.some((modal) => this.isModalOpen(modal))
      || this.isModalOpen(this.getSizeChartModal())
      || this.isBedsheetQuizOpen();
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  getBedsheetQuizOverlay() {
    return document.querySelector('.shopify-section.section-bedsheet-quiz')
      || document.querySelector('[data-pdp-bedsheet-quiz-modal]');
  }

  isBedsheetQuizOpen() {
    const overlay = this.getBedsheetQuizOverlay();
    return !!(overlay && overlay.classList.contains('is-open'));
  }

  bindSectionZoom() {
    const grid = this.root.querySelector('[data-pdp-gallery-desktop]');
    if (!grid) return;
    const desktopMq = window.matchMedia('(min-width: 750px)');

    const update = () => {
      if (!desktopMq.matches) {
        this.root.style.zoom = '';
        this.root.style.removeProperty('--pdp-zoom');
        return;
      }

      const styles = window.getComputedStyle(this.root);
      const readVar = (name, fallback) => parseFloat(styles.getPropertyValue(name)) || fallback;
      const infoWidth = readVar('--pdp-info-width', 561);
      const gap = readVar('--pdp-column-gap', 84);
      const viewportWidth = document.documentElement.clientWidth;
      const galleryWidthRaw = styles.getPropertyValue('--pdp-gallery-width').trim();
      // A % gallery resolves against the zoomed container, so it renders at the
      // same real width whatever the zoom; only the gap and info column shrink.
      // Scaling all three (viewport / needed) under-zoomed and left the info
      // column hanging ~48px off the right edge at 1024px.
      const galleryIsPercent = galleryWidthRaw.endsWith('%');
      const galleryWidth = galleryIsPercent
        ? viewportWidth * (parseFloat(galleryWidthRaw) / 100)
        : (parseFloat(galleryWidthRaw) || viewportWidth * 0.49);
      const fit = galleryIsPercent
        ? (viewportWidth - galleryWidth) / (gap + infoWidth)
        : viewportWidth / (galleryWidth + gap + infoWidth);
      const scale = Math.max(Math.min(1, fit), 0.7);

      if (scale >= 0.999) {
        this.root.style.zoom = '';
        this.root.style.removeProperty('--pdp-zoom');
      } else {
        this.root.style.zoom = String(scale);
        this.root.style.setProperty('--pdp-zoom', String(scale));
      }
    };

    window.addEventListener('resize', update);
    desktopMq.addEventListener('change', update);
    update();
  }

  bindGallery() {
    const prev = this.root.querySelector('[data-pdp-gallery-prev]');
    const next = this.root.querySelector('[data-pdp-gallery-next]');

    prev?.addEventListener('click', () => this.shiftGallery(-1));
    next?.addEventListener('click', () => this.shiftGallery(1));

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.setGalleryIndex(Number(thumb.dataset.pdpGalleryThumb));
      });
    });

    this.desktopItems.forEach((item) => {
      item.addEventListener('click', (event) => {
        this.setGalleryIndex(Number(item.dataset.pdpGalleryDesktopItem));
        const videoWrap = item.querySelector('[data-pdp-gallery-video]');
        if (videoWrap) {
          if (!videoWrap.classList.contains('is-playing')) {
            this.playGalleryVideo(videoWrap);
          }
          return;
        }
        const img = item.querySelector('img');
        if (img) this.openZoom(img);
      });
    });

    const loadMore = this.root.querySelector('[data-pdp-gallery-load-more]');
    loadMore?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const revealed = [...this.root
        .querySelectorAll('.pdp-gallery__grid-item.is-collapsed, .pdp-gallery__load-more-host.is-collapsed')];
      revealed.forEach((item) => item.classList.remove('is-collapsed'));
      loadMore.hidden = true;

      // Glide the pane to the first revealed image so the click visibly
      // does something. Rect deltas are visual px; scrollTop is the pane's
      // own px, which differ under the section zoom, hence the ratio.
      const pane = this.root.querySelector('[data-pdp-gallery-col]');
      const first = revealed[0];
      if (!pane || !first || pane.scrollHeight <= pane.clientHeight) return;
      const paneRect = pane.getBoundingClientRect();
      const ratio = paneRect.height ? pane.clientHeight / paneRect.height : 1;
      const top = pane.scrollTop + (first.getBoundingClientRect().top - paneRect.top) * ratio;
      pane.scrollTo({ top, behavior: 'smooth' });
    });

    this.galleryTrack?.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => this.syncGalleryUi(this.getActiveIndex()));
    }, { passive: true });

    this.syncGalleryUi(this.getActiveIndex());
  }

  bindGalleryVideos() {
    const muteVideos = this.root.dataset.pdpMuteGalleryVideos === 'true';
    const autoplayVideos = this.root.dataset.pdpAutoplayGalleryVideos === 'true';
    this.root.querySelectorAll('[data-pdp-gallery-video]').forEach((wrap) => {
      const playBtn = wrap.querySelector('[data-pdp-gallery-play]');
      const video = wrap.querySelector('video');
      if (video) {
        const shouldMute = muteVideos || autoplayVideos;
        const shouldLoop = video.hasAttribute('loop') || autoplayVideos;
        video.muted = shouldMute;
        video.controls = false;
        video.loop = shouldLoop;
        video.disablePictureInPicture = true;
        video.setAttribute('controlsList', 'nodownload noplaybackrate noremoteplayback');
        video.setAttribute('disablePictureInPicture', '');
        video.setAttribute('disableRemotePlayback', '');
        video.removeAttribute('controls');
        if (shouldLoop) video.setAttribute('loop', '');
        else video.removeAttribute('loop');
        if (shouldMute) video.setAttribute('muted', '');
        else video.removeAttribute('muted');
        if (!shouldLoop) {
          video.addEventListener('ended', () => this.resetGalleryVideo(wrap));
        }
      }
      const toggle = (event) => {
        event.preventDefault();
        this.toggleGalleryVideo(wrap);
      };
      playBtn?.addEventListener('click', toggle);
      playBtn?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.toggleGalleryVideo(wrap);
        }
      });
      wrap.addEventListener('click', toggle);
      if (autoplayVideos) this.autoplayGalleryVideoWhenLaidOut(wrap);
    });
  }

  // The mobile carousel and the desktop grid are both in the DOM and CSS hides one
  // of them, so playing every copy makes the hidden one download in full for
  // nothing. Only start the copy the layout is actually using, and pick the other
  // one up if the breakpoint changes.
  //
  // 19/10/2026 (speed): autoplay waits for the page's load event. Started straight away, the clip (4.5 Mbps,
  // ~4 MB in the first seconds) downloaded alongside everything else and held the page's first paint back to
  // ~13s on a slow phone connection. The full-size poster shows until then; the clip itself is unchanged.
  autoplayGalleryVideoWhenLaidOut(wrap) {
    this.afterPageLoad(() => this.whenLaidOut(wrap, (node) => this.playGalleryVideo(node)));
  }

  afterPageLoad(start) {
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  // Gallery slots come through pdp-section-gallery (wrapped in
  // [data-pdp-gallery-video]) while store media comes through pdp-media (a bare
  // video). Both duplicate across the mobile and desktop layouts, so both need the
  // laid-out check; only the wrapped ones have play/pause chrome to sync.
  bindStandaloneAutoplayVideos() {
    this.root.querySelectorAll('video.pdp-media__video--autoplay').forEach((video) => {
      if (video.closest('[data-pdp-gallery-video]')) return;
      this.afterPageLoad(() => this.whenLaidOut(video, (node) => {
        const played = node.play();
        if (played && played.catch) played.catch(() => {});
      }));
    });
  }

  whenLaidOut(el, start) {
    if (!el) return;
    if (this.isLaidOut(el)) {
      start(el);
      return;
    }
    this.deferredAutoplayVideos = this.deferredAutoplayVideos || new Map();
    this.deferredAutoplayVideos.set(el, start);
    if (this.deferredAutoplayBound) return;
    this.deferredAutoplayBound = true;
    window.addEventListener('resize', () => {
      window.clearTimeout(this.deferredAutoplayTimer);
      this.deferredAutoplayTimer = window.setTimeout(() => {
        this.deferredAutoplayVideos.forEach((begin, candidate) => {
          if (!this.isLaidOut(candidate)) return;
          this.deferredAutoplayVideos.delete(candidate);
          begin(candidate);
        });
      }, 150);
    }, { passive: true });
  }

  isLaidOut(el) {
    if (!el) return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  toggleGalleryVideo(wrap) {
    if (!wrap) return;
    const video = wrap.querySelector('video');
    if (!video) return;
    if (wrap.classList.contains('is-playing') && !video.paused) {
      video.pause();
      return;
    }
    if (wrap.classList.contains('is-playing') && video.paused) {
      const played = video.play();
      if (played && played.catch) played.catch(() => {});
      return;
    }
    this.playGalleryVideo(wrap);
  }

  playGalleryVideo(wrap) {
    if (!wrap || wrap.classList.contains('is-playing') || wrap.classList.contains('is-starting')) return;
    const video = wrap.querySelector('video');
    if (!video) return;
    const shouldMute = this.root.dataset.pdpMuteGalleryVideos === 'true'
      || this.root.dataset.pdpAutoplayGalleryVideos === 'true';
    video.muted = shouldMute;
    if (shouldMute) video.setAttribute('muted', '');
    video.controls = false;
    video.removeAttribute('controls');
    video.currentTime = 0;
    // 19/10/2026: the poster stays up until the first frame is ready, so the swap is seamless instead of
    // flashing the video's own small thumbnail (or black) while the first bytes arrive.
    if (video.readyState >= 3) wrap.classList.add('is-playing');
    else {
      wrap.classList.add('is-starting');
      const show = () => {
        if (!wrap.classList.contains('is-starting')) return;
        wrap.classList.remove('is-starting');
        wrap.classList.add('is-playing');
      };
      video.addEventListener('playing', show, { once: true });
    }
    const played = video.play();
    if (played && played.catch) played.catch(() => wrap.classList.remove('is-starting'));
  }

  resetGalleryVideo(wrap) {
    if (!wrap) return;
    const video = wrap.querySelector('video');
    wrap.classList.remove('is-playing', 'is-starting');
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  }

  syncGalleryUi(index) {
    const slides = this.getSlides();
    const lastIndex = Math.max(0, slides.length - 1);
    const clamped = Math.max(0, Math.min(index, lastIndex));
    const activeSlide = slides[clamped];
    const mediaIndex = activeSlide
      ? Number(activeSlide.dataset.mediaIndex)
      : clamped;

    this.thumbs.forEach((thumb) => {
      thumb.classList.toggle('is-active', Number(thumb.dataset.pdpGalleryThumb) === mediaIndex);
    });
    this.desktopItems.forEach((item) => {
      item.classList.toggle('is-active', Number(item.dataset.pdpGalleryDesktopItem) === mediaIndex);
    });

    const activeThumb = this.thumbs.find(
      (thumb) => Number(thumb.dataset.pdpGalleryThumb) === mediaIndex
    );
    // Only when the active photo changes: this runs on every frame of a swipe, and firing a
    // smooth scroll each frame made the thumbnail strip judder against its own snapping
    // (Justyn 14/10/2026).
    if (activeThumb && mediaIndex !== this.lastThumbIndex) {
      this.lastThumbIndex = mediaIndex;
      this.keepThumbVisible(activeThumb);
    }

    const prev = this.root.querySelector('[data-pdp-gallery-prev]');
    const next = this.root.querySelector('[data-pdp-gallery-next]');
    if (prev) prev.disabled = clamped <= 0;
    if (next) next.disabled = clamped >= lastIndex;
  }

  /* Scrolls the thumbnail rail itself instead of using scrollIntoView. The rail
     only renders on mobile, roughly 1300px down the page, so scrollIntoView's
     `block: 'nearest'` had to pull the whole window down to reveal it — which is
     what made the page jump to the gallery on load, since syncGalleryUi runs
     during init and again on every track scroll event. Adjusting scrollLeft on
     the rail can never move an ancestor scroller. */
  keepThumbVisible(thumb) {
    const strip = thumb.closest('[data-pdp-gallery-thumbs]');
    if (!strip) return;

    // One smooth move that centres the active thumbnail (clamped at either end), rather than
    // nudging it just into view.
    const stripRect = strip.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const max = strip.scrollWidth - strip.clientWidth;
    const target = strip.scrollLeft + (thumbRect.left - stripRect.left) - (stripRect.width - thumbRect.width) / 2;
    const left = Math.max(0, Math.min(max, Math.round(target)));
    if (Math.abs(left - strip.scrollLeft) > 1) strip.scrollTo({ left, behavior: 'smooth' });
  }

  getSlides() {
    return [...this.galleryTrack?.querySelectorAll('.pdp-gallery__slide:not(.hidden)') || []];
  }

  getActiveIndex() {
    if (!this.galleryTrack) return 0;
    // Step = one slide plus the gap, so it also works when the next photo peeks in (slides narrower than
    // the track, wellness buy box 17/10/2026); with full-width slides this is the track width as before.
    const first = this.galleryTrack.querySelector('.pdp-gallery__slide:not(.hidden)');
    const gap = parseFloat(getComputedStyle(this.galleryTrack).columnGap) || 0;
    const width = (first ? first.getBoundingClientRect().width + gap : this.galleryTrack.clientWidth) || 1;
    return Math.round(this.galleryTrack.scrollLeft / width) || 0;
  }

  setGalleryIndex(index) {
    const slides = this.getSlides();
    if (!slides.length || !this.galleryTrack) return;

    let visibleIndex = slides.findIndex((slide) => Number(slide.dataset.mediaIndex) === index);
    if (visibleIndex < 0) {
      visibleIndex = Math.max(0, Math.min(index, slides.length - 1));
    }

    const target = slides[visibleIndex];
    if (target) {
      const left = this.galleryTrack.scrollLeft
        + (target.getBoundingClientRect().left - this.galleryTrack.getBoundingClientRect().left);
      this.galleryTrack.scrollTo({ left, behavior: 'auto' });
    }
    this.syncGalleryUi(visibleIndex);
  }

  shiftGallery(direction) {
    this.setGalleryIndex(this.getActiveIndex() + direction);
  }

  // Single changing gallery slot: data-pdp-variant-position (1-based) or the
  // marked .is-variant-image tile shows the selected variant image. When
  // custom.gallery_variant_image is set, that override always fills whatever
  // slot currently holds the variant image (including after mobile promote).
  // Shared galleries (e.g. Thermasleep Both/One/Full) swap from
  // data-pdp-option-gallery-json by product id instead.
  // Bundle strip (AIR Dual, Justyn 16/10/2026 option 4): one dot per colour picker showing the
  // swatch picked there, and the colour names in the text ("Red + Silver", or "both Black").
  bindBundleStrip() {
    this.bundleStrip = this.root.querySelector('[data-pdp-bundle-strip]');
    this.syncBundleStrip();
  }

  syncBundleStrip() {
    const strip = this.bundleStrip;
    if (!strip) return;
    const picker = this.root.querySelector('[data-pdp-variant-picker]');
    if (!picker) return;

    const picked = [...picker.querySelectorAll('.pdp-variant-picker__option--color')].map((fieldset) => {
      const input = fieldset.querySelector('[data-pdp-option-input]:checked');
      if (!input) return null;
      const dot = fieldset.querySelector(`label[for="${input.id}"] .pdp-variant-picker__swatch-dot`);
      return { name: input.value, style: dot ? dot.getAttribute('style') : '' };
    }).filter(Boolean);

    strip.querySelectorAll('[data-pdp-bundle-dot]').forEach((dot, index) => {
      if (picked[index]) dot.setAttribute('style', picked[index].style || '');
    });

    const names = strip.querySelector('[data-pdp-bundle-colours]');
    if (names && picked.length) {
      const unique = [...new Set(picked.map((item) => item.name))];
      names.textContent = unique.length === 1 && picked.length > 1
        ? `both ${unique[0]}`
        : picked.map((item) => item.name).join(' + ');
    }
  }

  bindVariantGallery() {
    this.variantImageMap = this.parseJson('[data-pdp-variant-gallery-json]') || {};

    if (this.root.dataset.pdpHideVariantImage === 'true') {
      this.syncVideoProductImage();
      return;
    }

    const sharedGallery = this.root.dataset.pdpSharedGallery === 'true';
    this.variantGalleryMap = sharedGallery
      ? (this.parseJson('[data-pdp-option-gallery-json]') || {})
      : this.variantImageMap;
    if (!Object.keys(this.variantGalleryMap).length) {
      this.syncVideoProductImage();
      return;
    }

    this.variantSlots = this.resolveVariantSlots();
    if (!this.variantSlots.length) {
      this.syncVideoProductImage();
      return;
    }

    const currentId = sharedGallery
      ? this.root.dataset.pdpProductId
      : (this.variantInput?.value || (this.variants[0] && String(this.variants[0].id)));
    if (currentId) this.updateVariantSlot(currentId);
    this.syncVideoProductImage(currentId);
  }

  resolveVariantSlots() {
    const position = Number(this.root.dataset.pdpVariantPosition) || 1;
    const targets = [
      ['[data-pdp-gallery-track]', '.pdp-gallery__slide'],
      ['[data-pdp-gallery-desktop]', '.pdp-gallery__grid-item'],
      ['[data-pdp-gallery-thumbs]', '.pdp-gallery__thumb'],
    ];
    const slots = [];

    targets.forEach(([containerSel, itemSel]) => {
      const container = this.root.querySelector(containerSel);
      if (!container) return;

      const marked = container.querySelector(`${itemSel}.is-variant-image`);
      if (marked) {
        slots.push(marked);
        return;
      }

      const items = [...container.querySelectorAll(itemSel)].filter(
        (item) => !item.classList.contains('hidden') && !item.hasAttribute('hidden')
      );
      const slot = items[position - 1];
      if (!slot) return;
      slot.classList.add('is-variant-image');
      slots.push(slot);
    });

    return slots;
  }

  updateVariantSlot(variantId) {
    if (this.root.dataset.pdpHideVariantImage === 'true') return;

    const overrideEntry = (this.variantImageMap || {})[variantId]
      || (this.variantImageMap || {})[String(variantId)];
    let entry = overrideEntry && overrideEntry.override
      ? overrideEntry
      : ((this.variantGalleryMap || {})[variantId] || (this.variantGalleryMap || {})[String(variantId)]);

    if (!entry && this.root.dataset.pdpSharedGallery === 'true') {
      const productId = this.root.dataset.pdpProductId;
      entry = (this.variantGalleryMap || {})[productId]
        || (this.variantGalleryMap || {})[String(productId)];
    }

    // Re-resolve so the image follows the slot after mobile promote / reorder.
    this.variantSlots = this.resolveVariantSlots();
    if (!this.variantSlots.length) return;

    this.variantSlots.forEach((slot) => {
      const img = slot.querySelector('img');
      if (!img) return;

      if (!entry || !entry.src) {
        if (slot.classList.contains('is-variant-image') && img.dataset.pdpOriginalSrc) {
          img.src = img.dataset.pdpOriginalSrc;
          if (img.dataset.pdpOriginalSrcset) img.setAttribute('srcset', img.dataset.pdpOriginalSrcset);
          else img.removeAttribute('srcset');
          if (img.dataset.pdpOriginalSizes) img.setAttribute('sizes', img.dataset.pdpOriginalSizes);
        }
        return;
      }

      if (!img.dataset.pdpOriginalSrc) {
        img.dataset.pdpOriginalSrc = img.getAttribute('src') || img.src;
        if (img.getAttribute('srcset')) img.dataset.pdpOriginalSrcset = img.getAttribute('srcset');
        if (img.getAttribute('sizes')) img.dataset.pdpOriginalSizes = img.getAttribute('sizes');
      }

      img.removeAttribute('sizes');
      if (entry.srcset) img.setAttribute('srcset', entry.srcset);
      else img.removeAttribute('srcset');
      img.src = entry.src;
      slot.classList.add('is-variant-image');
    });
  }

  promoteVariantSlotOnMobile() {
    if (window.matchMedia('(min-width: 750px)').matches) return;
    if (this.root.dataset.pdpHideVariantImage === 'true') return;
    if (!this.galleryTrack) return;

    const slide = this.galleryTrack.querySelector('.pdp-gallery__slide.is-variant-image:not(.hidden)');
    if (!slide) return;

    const thumbsRoot = this.root.querySelector('[data-pdp-gallery-thumbs]');
    const thumb = thumbsRoot?.querySelector('.pdp-gallery__thumb.is-variant-image:not(.hidden)')
      || thumbsRoot?.querySelector(`[data-pdp-gallery-thumb="${slide.dataset.mediaIndex}"]`);

    const firstSlide = this.getSlides()[0];
    if (firstSlide && firstSlide !== slide) {
      this.galleryTrack.insertBefore(slide, firstSlide);
    }

    if (thumb && thumbsRoot) {
      const firstThumb = thumbsRoot.querySelector('.pdp-gallery__thumb:not(.hidden)');
      if (firstThumb && firstThumb !== thumb) {
        thumbsRoot.insertBefore(thumb, firstThumb);
      }
    }

    [...this.galleryTrack.querySelectorAll('.pdp-gallery__slide:not(.hidden)')].forEach((el, index) => {
      el.dataset.mediaIndex = String(index);
    });
    if (thumbsRoot) {
      [...thumbsRoot.querySelectorAll('.pdp-gallery__thumb:not(.hidden)')].forEach((el, index) => {
        el.dataset.pdpGalleryThumb = String(index);
      });
    }

    this.thumbs = [...this.root.querySelectorAll('[data-pdp-gallery-thumb]')];
    this.setGalleryIndex(0);
  }

  syncVideoProductImage(variantId) {
    const img = this.root.querySelector('[data-pdp-video-product-image], .pdp-video-modal__product-image img');
    if (!img) return;

    const id = variantId
      || this.variantInput?.value
      || (this.variants[0] && String(this.variants[0].id));
    if (!id) return;

    const map = this.variantImageMap || this.parseJson('[data-pdp-variant-gallery-json]') || {};
    const entry = map[id] || map[String(id)];
    if (!entry || !entry.src) return;

    img.removeAttribute('sizes');
    if (entry.srcset) img.setAttribute('srcset', entry.srcset);
    else img.removeAttribute('srcset');
    img.src = entry.src;
  }

  focusVariantMedia(variantId) {
    const mediaId = this.variantMediaMap[variantId];
    if (!mediaId) return;

    const slide = this.galleryTrack?.querySelector(`.pdp-gallery__slide[data-media-id="${mediaId}"]`);
    if (!slide) return;

    const index = Number(slide.dataset.mediaIndex);
    if (Number.isNaN(index)) return;

    // Mobile hero track + active-state sync (thumbs / desktop grid).
    this.setGalleryIndex(index);

    // Desktop: the grid is capped, so most colour images aren't tiles in it.
    // Swap the hero (first) grid image in place, reusing the already-rendered
    // slide image so every colour updates regardless of the row cap.
    if (window.matchMedia('(min-width: 750px)').matches) {
      const heroImg = this.desktopItems[0]?.querySelector('img');
      const sourceImg = slide.querySelector('img');
      if (heroImg && sourceImg) {
        heroImg.srcset = sourceImg.srcset || '';
        if (sourceImg.sizes) heroImg.sizes = sourceImg.sizes;
        heroImg.src = sourceImg.src;
      }
    }
  }

  bindGalleryZoom() {
    const zoomBtn = this.root.querySelector('[data-pdp-gallery-zoom]');
    if (!zoomBtn) return;

    zoomBtn.addEventListener('click', () => this.openZoom());
    document.addEventListener('keydown', (event) => {
      if (!this.zoomOverlay || this.zoomOverlay.hidden) return;
      if (event.key === 'Escape') this.closeZoom();
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.shiftZoom(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.shiftZoom(1);
      }
    });
  }

  // Current image shown in the mobile hero (falls back to the first slide).
  getActiveImage() {
    const slides = this.getSlides();
    if (!slides.length) return null;
    const active = slides[this.getActiveIndex()] || slides[0];
    return active.querySelector('img');
  }

  getZoomableSlides() {
    return this.getSlides().filter((slide) => {
      if (slide.querySelector('[data-pdp-gallery-video]')) return false;
      return Boolean(slide.querySelector('img'));
    });
  }

  getZoomSlideForImage(img) {
    if (!img) return null;
    const fromSlide = img.closest('.pdp-gallery__slide');
    if (fromSlide) return fromSlide;

    const gridItem = img.closest('[data-pdp-gallery-desktop-item]');
    if (!gridItem) return null;
    const mediaIndex = Number(gridItem.dataset.pdpGalleryDesktopItem);
    return this.getSlides().find((slide) => Number(slide.dataset.mediaIndex) === mediaIndex) || null;
  }

  ensureZoomOverlay() {
    if (this.zoomOverlay) return this.zoomOverlay;

    const overlay = document.createElement('div');
    overlay.className = 'pdp-gallery__zoom-overlay';
    overlay.hidden = true;
    overlay.innerHTML = `
      <div class="pdp-gallery__zoom-toolbar">
        <div class="pdp-gallery__zoom-nav" data-pdp-zoom-nav hidden>
          <button type="button" class="pdp-gallery__arrow" data-pdp-zoom-prev aria-label="Previous image">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M10 3L5 8l5 5"/>
            </svg>
          </button>
          <button type="button" class="pdp-gallery__arrow" data-pdp-zoom-next aria-label="Next image">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M6 3l5 5-5 5"/>
            </svg>
          </button>
        </div>
        <button type="button" class="pdp-gallery__zoom-close" aria-label="Close zoom">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
      <div class="pdp-gallery__zoom-stage" data-pdp-zoom-stage>
        <div class="pdp-gallery__zoom-frame" data-pdp-zoom-frame>
          <img class="pdp-gallery__zoom-img" data-pdp-zoom-img alt="">
        </div>
      </div>`;

    overlay.querySelector('.pdp-gallery__zoom-close').addEventListener('click', () => this.closeZoom());
    overlay.querySelector('[data-pdp-zoom-prev]').addEventListener('click', (event) => {
      event.stopPropagation();
      this.shiftZoom(-1);
    });
    overlay.querySelector('[data-pdp-zoom-next]').addEventListener('click', (event) => {
      event.stopPropagation();
      this.shiftZoom(1);
    });
    overlay.addEventListener('click', (event) => {
      if (event.target.closest('[data-pdp-zoom-frame], .pdp-gallery__zoom-toolbar')) return;
      this.closeZoom();
    });
    overlay.querySelector('[data-pdp-zoom-img]').addEventListener('click', (event) => {
      event.stopPropagation();
      event.currentTarget.classList.toggle('is-zoomed');
    });

    document.body.appendChild(overlay);
    this.zoomOverlay = overlay;
    return overlay;
  }

  syncZoomNav() {
    if (!this.zoomOverlay) return;
    const slides = this.getZoomableSlides();
    const nav = this.zoomOverlay.querySelector('[data-pdp-zoom-nav]');
    const prev = this.zoomOverlay.querySelector('[data-pdp-zoom-prev]');
    const next = this.zoomOverlay.querySelector('[data-pdp-zoom-next]');
    const index = this.zoomSlideIndex || 0;
    const lastIndex = Math.max(0, slides.length - 1);

    if (nav) nav.hidden = slides.length < 2;
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= lastIndex;
  }

  showZoomSlide(zoomableIndex, fallbackImg) {
    const slides = this.getZoomableSlides();
    const slide = slides[zoomableIndex];
    const img = slide?.querySelector('img') || fallbackImg;
    if (!img || !this.zoomOverlay) return;

    this.zoomSlideIndex = zoomableIndex;
    const zoomImg = this.zoomOverlay.querySelector('[data-pdp-zoom-img]');
    zoomImg.classList.remove('is-zoomed');
    zoomImg.srcset = img.srcset || '';
    zoomImg.sizes = '100vw';
    zoomImg.src = img.currentSrc || img.src;
    zoomImg.alt = img.alt || '';

    if (slide?.dataset.mediaIndex != null) {
      this.setGalleryIndex(Number(slide.dataset.mediaIndex));
    }
    this.syncZoomNav();
  }

  shiftZoom(direction) {
    if (!this.zoomOverlay || this.zoomOverlay.hidden) return;
    const slides = this.getZoomableSlides();
    if (slides.length < 2) return;
    const next = Math.max(0, Math.min(slides.length - 1, (this.zoomSlideIndex || 0) + direction));
    if (next === this.zoomSlideIndex) return;
    this.showZoomSlide(next);
  }

  openZoom(image) {
    const img = image || this.getActiveImage();
    if (!img) return;

    this.ensureZoomOverlay();

    const slide = this.getZoomSlideForImage(img);
    const zoomable = this.getZoomableSlides();
    let zoomableIndex = slide ? zoomable.indexOf(slide) : -1;
    if (zoomableIndex < 0) {
      zoomableIndex = Math.max(0, this.getActiveIndex());
      const activeSlide = this.getSlides()[zoomableIndex];
      zoomableIndex = activeSlide ? zoomable.indexOf(activeSlide) : 0;
      if (zoomableIndex < 0) zoomableIndex = 0;
    }

    this.showZoomSlide(zoomableIndex, img);
    this.zoomOverlay.hidden = false;
    this.syncBodyScrollLock();
  }

  closeZoom() {
    if (!this.zoomOverlay) return;
    this.zoomOverlay.hidden = true;
    this.zoomOverlay.querySelector('[data-pdp-zoom-img]')?.classList.remove('is-zoomed');
    this.syncBodyScrollLock();
  }

  bindConfigure() {
    const prev = this.root.querySelector('[data-pdp-configure-prev]');
    const next = this.root.querySelector('[data-pdp-configure-next]');
    const track = this.root.querySelector('[data-pdp-configure-track]');

    if (track) {
      track.scrollLeft = 0;
    }

    prev?.addEventListener('click', () => {
      track?.scrollBy({ left: -(track.clientWidth * 0.8), behavior: 'smooth' });
    });

    next?.addEventListener('click', () => {
      track?.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' });
    });

    this.configureCards.forEach((card) => {
      card.querySelector('[data-pdp-configure-add]')?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.syncConfigureAddonVariants();
        card.classList.toggle('is-selected');
        event.currentTarget.setAttribute('aria-pressed', String(card.classList.contains('is-selected')));
        this.updateSelectedAddons();
        this.updateCtaPrice();
      });
    });

    // Row layout (credibility buy box): "See N more add-ons" shows the rest.
    this.root.querySelector('[data-pdp-addrow-more]')?.addEventListener('click', (event) => {
      this.root.querySelectorAll('[data-pdp-addrow-extra]').forEach((row) => { row.hidden = false; });
      event.currentTarget.hidden = true;
    });

    this.syncConfigureAddonVariants();
    this.syncConfigureSubtitles();
    window.addEventListener('resize', () => this.syncConfigureSubtitles());
    if (document.fonts?.ready) document.fonts.ready.then(() => this.syncConfigureSubtitles());
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this.syncConfigureSubtitles());
      this.configureCards.forEach((card) => {
        const title = card.querySelector('.pdp-configure__card-title');
        if (title) observer.observe(title);
      });
    }
  }

  syncConfigureSubtitles() {
    this.configureCards.forEach((card) => {
      const title = card.querySelector('.pdp-configure__card-title');
      const subtitle = card.querySelector('.pdp-configure__card-subtitle');
      if (!title || !subtitle) return;

      const styles = window.getComputedStyle(title);
      const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5 || 24;
      const lines = Math.round(title.getBoundingClientRect().height / lineHeight);
      subtitle.hidden = lines > 1;
    });
  }

  matchAddonVariant(mainVariant, mainOptionNames, addonOptionNames, addonVariants) {
    if (!mainVariant || !Array.isArray(addonVariants) || !addonVariants.length) return null;

    const mainByName = {};
    (mainOptionNames || []).forEach((name, index) => {
      mainByName[String(name).toLowerCase()] = String(mainVariant.options?.[index] || '').trim().toLowerCase();
    });

    const exact = addonVariants.find((variant) => {
      if (variant.available === false) return false;
      let compared = 0;
      let matches = 0;
      (addonOptionNames || []).forEach((name, index) => {
        const key = String(name).toLowerCase();
        if (!(key in mainByName) || !mainByName[key]) return;
        compared += 1;
        const addonValue = String(variant.options?.[index] || '').trim().toLowerCase();
        if (addonValue && addonValue === mainByName[key]) matches += 1;
      });
      return compared > 0 && matches === compared;
    });
    if (exact) return exact;

    const mainValues = new Set(
      (mainVariant.options || []).map((value) => String(value).trim().toLowerCase()).filter(Boolean)
    );
    return addonVariants.find((variant) => {
      if (variant.available === false) return false;
      return (variant.options || []).some((value) => mainValues.has(String(value).trim().toLowerCase()));
    }) || null;
  }

  syncConfigureAddonVariants() {
    if (!this.configureCards.length) return;

    const mainVariant = this.getCurrentVariant();
    if (!mainVariant) return;

    const mainOptionNames = this.parseJson('[data-pdp-options-json]') || [];

    this.configureCards.forEach((card) => {
      const variantsNode = card.querySelector('[data-pdp-configure-variants]');
      const optionNamesNode = card.querySelector('[data-pdp-configure-option-names]');
      if (!variantsNode) return;

      let addonVariants = [];
      let addonOptionNames = [];
      try {
        addonVariants = JSON.parse(variantsNode.textContent);
        addonOptionNames = optionNamesNode ? JSON.parse(optionNamesNode.textContent) : [];
      } catch (error) {
        return;
      }
      if (!Array.isArray(addonVariants) || addonVariants.length < 2) return;

      const matched = this.matchAddonVariant(mainVariant, mainOptionNames, addonOptionNames, addonVariants);
      if (!matched) return;

      card.dataset.variantId = matched.id;
      card.dataset.addonPrice = matched.price;

      const priceEl = card.querySelector('[data-pdp-configure-price], .pdp-configure__price-current');
      if (priceEl) priceEl.textContent = this.formatMoney(matched.price);

      const mediaOverride = card.querySelector('.pdp-configure__card-media--override');
      const img = card.querySelector('.pdp-configure__card-media img, [data-pdp-configure-media] img');
      if (!mediaOverride && img && matched.featured_image?.src) {
        img.src = matched.featured_image.src;
        img.srcset = '';
      }
    });
  }

  updateSelectedAddons() {
    this.selectedAddons = this.configureCards
      .filter((card) => card.classList.contains('is-selected'))
      .map((card) => ({
        id: Number(card.dataset.variantId),
        quantity: 1,
        price: parseInt(card.dataset.addonPrice, 10) || 0,
      }));
  }

  formatMoney(cents) {
    if (typeof formatMoney === 'function') {
      const moneyFormat = typeof currencyFormat !== 'undefined' ? currencyFormat : '';
      return formatMoney(cents, moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  formatMoneyTrim(cents) {
    if (cents % 100 !== 0) return this.formatMoney(cents);
    if (typeof formatMoney !== 'function') return String(cents / 100);

    const moneyFormat = typeof currencyFormat !== 'undefined' ? currencyFormat : '';
    const wholeMoneyFormat = moneyFormat.includes('amount_no_decimals')
      ? moneyFormat
      : moneyFormat.replace('amount', 'amount_no_decimals');

    return formatMoney(cents, wholeMoneyFormat);
  }

  formatInstallmentMoney(cents) {
    if (cents % 100 !== 0) return this.formatMoney(cents);
    if (typeof formatMoney !== 'function') return String(cents / 100);

    const moneyFormat = typeof currencyFormat !== 'undefined' ? currencyFormat : '';
    const wholeMoneyFormat = moneyFormat.includes('amount_no_decimals')
      ? moneyFormat
      : moneyFormat.replace('amount', 'amount_no_decimals');

    return formatMoney(cents, wholeMoneyFormat);
  }

  formatSizeOptionLabel(value) {
    return String(value || '')
      .toLowerCase()
      .split('-')
      .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
      .join('-');
  }

  getCurrentVariant() {
    const id = Number(this.variantInput?.value);
    return this.variants.find((variant) => variant.id === id) || this.variants[0];
  }

  getKlarnaInstallmentCents(totalCents, parts = 3) {
    return Math.round(totalCents / parts / 100) * 100;
  }

  bindProductOptions() {
    const links = [...this.root.querySelectorAll('[data-pdp-product-option]')];
    if (!links.length) return;

    if (!PdpRedesign.popstateBound) {
      PdpRedesign.popstateBound = true;
      window.addEventListener('popstate', () => window.location.reload());
    }

    links.forEach((link) => {
      link.addEventListener('click', async (event) => {
        event.preventDefault();
        const url = link.getAttribute('href');
        if (!url || this.root.classList.contains('is-swapping')) return;

        this.root.classList.add('is-swapping');
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newRoot = doc.querySelector('[data-pdp-redesign]');
          if (!newRoot) throw new Error('PDP root missing in response');

          const keepGallery = this.root.dataset.pdpSharedGallery === 'true'
            || newRoot.dataset.pdpSharedGallery === 'true';
          if (keepGallery) {
            const oldGallery = this.root.querySelector('.pdp-redesign__gallery-col');
            const newGallery = newRoot.querySelector('.pdp-redesign__gallery-col');
            if (oldGallery && newGallery) {
              newGallery.replaceWith(oldGallery.cloneNode(true));
            }
            newRoot.dataset.pdpSharedGallery = 'true';
          }

          this.root.replaceWith(newRoot);
          document.querySelectorAll('body > [data-pdp-size-chart-modal]').forEach((node) => node.remove());
          window.history.pushState({}, '', url);
          if (doc.title) document.title = doc.title;
          new PdpRedesign(newRoot);
          newRoot.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        } catch (error) {
          console.error('PdpRedesign: product option swap failed', error);
          window.location.href = url;
        }
      });
    });
  }

  isPreorderVariant(variant) {
    if (this.ctaMode === 'preorder') return true;
    return Boolean(variant && variant.available && this.variantPreorderMap[variant.id]);
  }

  updateCtaPrice() {
    if (this.ctaMode === 'preorder') return;

    const cta = this.root.querySelector('[data-pdp-cta]');
    const variant = this.getCurrentVariant();
    if (!cta || !variant) return;

    if (this.isPreorderVariant(variant)) {
      cta.textContent = cta.dataset.preorderLabel || 'Pre-order today';
      cta.classList.add('pdp-redesign__cta--preorder');
      cta.disabled = false;
      this.updatePaymentTerms();
      return;
    }
    cta.classList.remove('pdp-redesign__cta--preorder');

    const addonTotal = this.selectedAddons.reduce((sum, item) => sum + item.price, 0);
    const total = variant.price + addonTotal;

    if (variant.available) {
      cta.textContent = `Buy Now | ${this.formatMoney(total)}`;
      cta.disabled = false;
    }
    this.updatePaymentTerms();
  }

  bindVariantPicker() {
    const picker = this.root.querySelector('[data-pdp-variant-picker]');
    if (!picker) return;

    picker.querySelectorAll('[data-pdp-option-input]').forEach((input) => {
      input.addEventListener('change', () => {
        const position = Number(input.dataset.pdpOptionInput);
        const valueLabel = picker.querySelector(`[data-pdp-option-value="${position}"]`);
        if (valueLabel) {
          const optionEl = input.closest('.pdp-variant-picker__option');
          valueLabel.textContent = optionEl?.classList.contains('pdp-variant-picker__option--size')
            ? this.formatSizeOptionLabel(input.value)
            : input.value;
        }

        picker.querySelectorAll('.pdp-variant-picker__swatch').forEach((swatch) => {
          const linked = picker.querySelector(`#${swatch.getAttribute('for')}`);
          swatch.classList.toggle('is-selected', Boolean(linked?.checked));
        });

        this.applySelectedVariant();
      });
    });
  }

  applySelectedVariant() {
    const picker = this.root.querySelector('[data-pdp-variant-picker]');
    if (!picker || !this.variants.length) return;

    const selectedOptions = [];
    for (let i = 1; i <= 3; i += 1) {
      const input = picker.querySelector(`[data-pdp-option-input="${i}"]:checked`);
      if (input) selectedOptions.push(input.value);
    }

    const variant = this.variants.find((item) => {
      return item.options.every((option, index) => option === selectedOptions[index]);
    });

    if (!variant) return;

    if (this.variantInput) this.variantInput.value = variant.id;
    this.syncConfigureAddonVariants();
    this.updateSelectedAddons();
    this.updatePriceUi(variant);
    this.syncBundleStrip();
    this.updateVariantSlot(variant.id);
    this.promoteVariantSlotOnMobile();
    this.syncVideoProductImage(variant.id);

    if (this.updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  updatePriceUi(variant) {
    const price = this.root.querySelector('[data-pdp-price]');
    const compare = this.root.querySelector('[data-pdp-compare-price]');
    const saveBadge = this.root.querySelector('[data-pdp-save-badge]');
    const cta = this.root.querySelector('[data-pdp-cta]');

    if (price) price.textContent = this.formatMoney(variant.price);

    const giftValue = Number(this.root.querySelector('[data-pdp-gift-value]')?.dataset.pdpGiftValue) || 0;
    const compareAt = variant.compare_at_price > variant.price ? variant.compare_at_price : 0;
    // Bundles: the items bought separately, added up in Liquid (bundle_items section setting).
    const bundleBefore = Number(this.root.querySelector('[data-pdp-bundle-before]')?.dataset.pdpBundleBefore) || 0;
    const isBundle = bundleBefore > variant.price;
    let beforePrice = giftValue > 0 ? variant.price + giftValue : compareAt;
    if (!beforePrice && isBundle) beforePrice = bundleBefore;
    let saveAmount = beforePrice > variant.price ? beforePrice - variant.price : 0;

    const bundleSaving = this.root.querySelector('[data-pdp-bundle-saving]');
    if (bundleSaving && isBundle) bundleSaving.textContent = this.formatMoney(bundleBefore - variant.price);

    // Savings round up to whole pounds and the before price is rebuilt from that
    // rounded saving, mirroring the Liquid so the badge and struck-through price
    // stay in step on variant change.
    if (saveAmount > 0 && !isBundle) {
      saveAmount = Math.ceil(saveAmount / 100) * 100;
      beforePrice = variant.price + saveAmount;
    }

    if (compare) {
      if (beforePrice > variant.price) {
        compare.textContent = this.formatMoneyTrim(beforePrice);
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }

    if (saveBadge) {
      // A bundle's strip already says what it saves.
      if (saveAmount > 0 && !isBundle) {
        saveBadge.textContent = `Save ${this.formatMoneyTrim(saveAmount)}`;
        saveBadge.hidden = false;
      } else {
        saveBadge.textContent = '';
        saveBadge.hidden = true;
      }
    }

    if (cta) {
      if (this.ctaMode === 'preorder') {
        cta.disabled = !variant.available;
      } else if (variant.available) {
        this.updateCtaPrice();
      } else {
        cta.textContent = 'Sold out';
        cta.classList.remove('pdp-redesign__cta--preorder');
        cta.disabled = true;
      }
    }

    this.updatePaymentTerms();
  }

  updatePaymentTerms() {
    const amounts = this.root.querySelectorAll('[data-pdp-klarna-amount]');
    if (!amounts.length) return;

    const variant = this.getCurrentVariant();
    if (!variant) return;

    const addonTotal = this.selectedAddons.reduce((sum, item) => sum + item.price, 0);
    const total = variant.price + addonTotal;

    amounts.forEach((amount) => {
      const parts = Number(amount.getAttribute('data-pdp-klarna-parts') || 3);
      amount.textContent = this.formatInstallmentMoney(this.getKlarnaInstallmentCents(total, parts));
    });

    this.updateBnplSchedule(total);
  }

  /**
   * Klarna / Clearpay pop-ups (credibility buy box). Each instalment rounds up to
   * the penny and the last takes the remainder, so the schedule adds up to the
   * total: £299.99 is £100 + £100 + £99.99, or £75 x 3 + £74.99. Matches the
   * Liquid in snippets/pdp-bnpl-modal.liquid.
   */
  updateBnplSchedule(total) {
    this.bnplModals.forEach((modal) => {
      modal.querySelectorAll('[data-pdp-bnpl-total]').forEach((node) => {
        node.textContent = this.formatMoney(total);
      });
      modal.querySelectorAll('[data-pdp-bnpl-amount]').forEach((node) => {
        const parts = Number(node.dataset.parts || 3);
        const part = Math.ceil(total / parts);
        const cents = node.hasAttribute('data-last') ? total - part * (parts - 1) : part;
        node.textContent = node.hasAttribute('data-short') ? this.formatInstallmentMoney(cents) : this.formatMoney(cents);
      });
    });
  }

  /**
   * "Compare models" sliders inside the How we compare dropdown (snippets/pdp-compare-accordion.liquid).
   * Same arrows and progress line as the compare pop-up's carousel. The slider starts hidden behind
   * the "Other brands" tab, so it measures again whenever its view is switched on.
   */
  bindCmpSliders() {
    this.root.querySelectorAll('[data-pdp-cmp-slider]').forEach((slider) => {
      const track = slider.querySelector('[data-pdp-cmp-track]');
      const prev = slider.querySelector('[data-pdp-cmp-prev]');
      const next = slider.querySelector('[data-pdp-cmp-next]');
      const progress = slider.querySelector('[data-pdp-cmp-progress]');
      const pagination = slider.querySelector('.pdp-cmp__pagination');
      if (!track) return;

      const step = () => {
        const card = track.querySelector('.pdp-compare-modal__card');
        const gap = parseFloat(getComputedStyle(track).columnGap || '16') || 16;
        return card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.5;
      };

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        // Two models fit side by side, so there is nothing to slide: hide the line and arrows.
        if (pagination && track.clientWidth > 0) pagination.hidden = max <= 1;
        if (progress) {
          const visible = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
          const travelled = max > 0 ? track.scrollLeft / max : 0;
          const fill = Math.min(1, Math.max(visible, 0.15));
          progress.style.width = `${fill * 100}%`;
          progress.style.left = `${travelled * (100 - fill * 100)}%`;
        }
        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= max - 1;
      };

      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      slider.closest('.pdp-cmp')?.querySelectorAll('.pdp-cmp__radio').forEach((radio) => {
        radio.addEventListener('change', () => window.requestAnimationFrame(update));
      });
      window.requestAnimationFrame(update);
    });
  }

  bindBnplModal() {
    if (!this.bnplModals.length) return;
    this.root.querySelectorAll('[data-pdp-bnpl-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openBnplModal(btn.dataset.pdpBnplOpen, btn));
    });
    this.bnplModals.forEach((modal) => {
      modal.querySelectorAll('[data-pdp-bnpl-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeBnplModal(modal));
      });
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.bnplModals.filter((modal) => !modal.hidden).forEach((modal) => this.closeBnplModal(modal));
    });
    // Research pop-up: Benefits and How it works tabs.
    this.bnplModals.forEach((modal) => {
      modal.querySelectorAll('[data-pdp-research-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.showResearchTab(modal, tab.dataset.pdpResearchTab));
      });
    });
  }

  showResearchTab(modal, name) {
    const tabs = modal.querySelectorAll('[data-pdp-research-tab]');
    if (!tabs.length) return;
    tabs.forEach((tab) => {
      const on = tab.dataset.pdpResearchTab === name;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    modal.querySelectorAll('[data-pdp-research-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.pdpResearchPanel !== name;
    });
    const dialog = modal.querySelector('.pdp-bnpl__dialog');
    const strip = modal.querySelector('.pdp-research__tabs');
    // Keep the tabs in view when switching from further down the pop-up.
    if (dialog && strip && dialog.scrollTop > strip.offsetTop) dialog.scrollTop = strip.offsetTop;
  }

  openBnplModal(name, opener) {
    const modal = this.bnplModals.find((item) => item.dataset.pdpBnplModal === name);
    if (!modal) return;
    this.bnplOpener = opener || null;
    modal.hidden = false;
    this.syncBodyScrollLock();
    // Focus the card, not the close button, so no focus ring shows on tap.
    const dialog = modal.querySelector('.pdp-bnpl__dialog');
    dialog?.focus();
    // A benefit in the buy box grid opens the research pop-up at its own section.
    if (dialog) {
      const jump = opener?.dataset.pdpResearchJump;
      const target = jump && modal.querySelector(`[data-pdp-research-target="${jump}"]`);
      this.showResearchTab(modal, 'benefits');
      dialog.scrollTop = 0;
      if (target) {
        dialog.scrollTop = target.getBoundingClientRect().top - dialog.getBoundingClientRect().top - 16;
      }
    }
  }

  closeBnplModal(modal) {
    modal.hidden = true;
    this.syncBodyScrollLock();
    this.bnplOpener?.focus();
  }

  bindCartSubmit() {
    if (!this.productForm) return;

    const addSelectedItemsToCart = async (event) => {
      if (!this.root.isConnected) return;

      if (event.type === 'click') {
        const cta = event.target.closest?.('[data-pdp-cta]');
        if (!cta || !this.root.contains(cta)) return;
      } else if (event.type === 'submit') {
        if (event.target !== this.productForm) return;
      } else {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.isAddingToCart) return;
      this.isAddingToCart = true;

      const cta = this.root.querySelector('[data-pdp-cta]');
      if (cta) cta.disabled = true;

      try {
        const variantId = Number(this.variantInput?.value);
        if (!variantId) return;

        this.updateSelectedAddons();

        // Anything an app has put into the form has to travel with the main item, because this
        // handler replaces the browser's form submit and posts JSON instead. Globo Pre-order is
        // the case that bit: it adds a selling_plan radio and properties[_is_preorder] to the
        // form, and until 2026-09-21 both were dropped here, so a pre-ordered Infra Knee reached
        // the cart (and the order) as an ordinary purchase — no pre-order flag, no selling plan,
        // no Globo order tag. Underscore-prefixed properties are the Shopify convention for
        // "carry but do not display", and the cart line item hides them accordingly.
        const mainItem = { id: variantId, quantity: 1 };
        const form = this.productForm;
        if (form) {
          const sellingPlanInput = form.querySelector('input[name="selling_plan"]:checked')
            || form.querySelector('input[name="selling_plan"][type="hidden"]')
            || form.querySelector('select[name="selling_plan"]');
          const sellingPlan = sellingPlanInput?.value;
          if (sellingPlan) mainItem.selling_plan = Number(sellingPlan) || sellingPlan;

          const properties = {};
          form.querySelectorAll('[name^="properties["]').forEach((input) => {
            if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
            const key = input.name.slice('properties['.length, -1);
            if (key && input.value !== '') properties[key] = input.value;
          });
          // Globo pre-order dispatch date as a VISIBLE property, so it reaches checkout, order pages,
          // admin and order emails ("Dispatch: From 9 Oct 2026"). The lookup lives in
          // snippets/globo.preorder.custom.liquid, which also dates every other add-to-cart on the
          // site; calling it here just makes this buy box independent of the fetch hook.
          if (sellingPlan && !properties.Dispatch && typeof window.PulsioGloboDispatch === 'function') {
            const dispatch = window.PulsioGloboDispatch(sellingPlan, this.root.dataset.pdpProductId, variantId);
            if (dispatch) properties.Dispatch = dispatch;
          }

          if (Object.keys(properties).length) mainItem.properties = properties;
        }

        const items = [
          mainItem,
          ...this.selectedAddons
            .filter((item) => Number.isFinite(item.id) && item.id > 0)
            .map(({ id, quantity }) => ({ id, quantity: quantity || 1 })),
        ];

        const response = await fetch(window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const errorBody = await response.text().catch(() => '');
          throw new Error(errorBody || 'Add to cart failed');
        }

        // UpCart only. This used to test window.upcartOpenCart ONCE and, if it was not yet
        // defined, redirect to /cart — the theme cart. UpCart's embed often has not finished
        // loading at click time, so the fallback fired on a perfectly healthy cart. That is why
        // the Halo page went to the theme cart while other PDPs, which submit the form normally
        // and let UpCart intercept, were fine (Justyn 2026-08-15).
        const upcartReady = async () => {
          for (let i = 0; i < 40; i += 1) {
            if (typeof window.upcartOpenCart === 'function') return true;
            await new Promise((r) => setTimeout(r, 50));
          }
          return typeof window.upcartOpenCart === 'function';
        };

        if (typeof window.upcartRefreshCart === 'function') {
          try { await window.upcartRefreshCart(); } catch (error) { /* no-op */ }
        }

        if (await upcartReady()) {
          if (typeof window.upcartRefreshCart === 'function') {
            try { await window.upcartRefreshCart(); } catch (error) { /* no-op */ }
          }
          window.upcartOpenCart();
          return;
        }

        // Still nothing after 2s: the item IS in the cart, so do NOT bounce the customer to the
        // theme cart. Nudge UpCart the way its own embed listens for, and leave them on the page.
        document.dispatchEvent(new CustomEvent('upcart:refresh'));
        console.warn('PdpRedesign: UpCart did not initialise; item added, staying on page');
      } catch (error) {
        // A genuine add-to-cart FAILURE. Previously this also redirected to /cart, which looked
        // identical to the UpCart fallback above and made the two impossible to tell apart.
        console.error('PdpRedesign: add to cart failed', error);
      } finally {
        this.isAddingToCart = false;
        if (cta && this.getCurrentVariant()?.available !== false) cta.disabled = false;
      }
    };

    // Document capture runs before app listeners (e.g. Upcart) that only add the main product.
    document.addEventListener('click', addSelectedItemsToCart, true);
    this.productForm.addEventListener('submit', addSelectedItemsToCart, true);
  }

  bindVideoModal() {
    if (!this.videoModal || !this.videoMedia) return;

    this.videoTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.openVideo(index));
    });

    this.root.querySelectorAll('[data-pdp-video-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeVideo());
    });

    this.root.querySelector('[data-pdp-video-add]')?.addEventListener('click', () => {
      this.productForm?.requestSubmit();
      this.closeVideo();
    });

    this.root.querySelector('[data-pdp-video-pause]')?.addEventListener('click', () => this.toggleVideoPlay());
    // Instagram-style taps (Justyn, 07/10/2026): the left third goes back a clip, the right third skips forward, the
    // middle pauses. stepVideo wraps, so skipping past the last clip loops to the first and back from the first to the last.
    this.videoMedia.addEventListener('click', (event) => {
      if (Date.now() - (this.videoSwipedAt || 0) < 400) return;
      const rect = this.videoMedia.getBoundingClientRect();
      const x = rect.width ? (event.clientX - rect.left) / rect.width : 0.5;
      if (this.videoTriggers.length > 1 && x < 0.3) this.stepVideo(-1);
      else if (this.videoTriggers.length > 1 && x > 0.7) this.stepVideo(1);
      else this.toggleVideoPlay();
    });

    // Reels-style swipe (Justyn, 07/10/2026): up for the next clip, down for the previous one, or down on the first
    // clip to close.
    const reelsDialog = this.videoModal.querySelector('.pdp-video-modal__dialog');
    let touchStartX = null;
    let touchStartY = null;
    reelsDialog?.addEventListener('touchstart', (event) => {
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    }, { passive: true });
    reelsDialog?.addEventListener('touchend', (event) => {
      if (touchStartY === null) return;
      const dx = event.changedTouches[0].clientX - touchStartX;
      const dy = event.changedTouches[0].clientY - touchStartY;
      touchStartX = null;
      touchStartY = null;
      if (Math.abs(dy) < 60 || Math.abs(dy) < Math.abs(dx)) return;
      this.videoSwipedAt = Date.now();
      if (dy < 0) this.stepVideo(1);
      else if (this.currentVideoIndex > 0) this.stepVideo(-1);
      else this.closeVideo();
    }, { passive: true });

    this.root.querySelector('[data-pdp-video-mute]')?.addEventListener('click', () => {
      const video = this.videoMedia.querySelector('video');
      if (!video) return;
      video.muted = !video.muted;
      this.videoModal.classList.toggle('is-unmuted', !video.muted);
      // With sound on by default, a visitor who mutes keeps the next clips muted too.
      this.videoSoundOff = video.muted;
    });

    this.videoModal.querySelector('[data-pdp-video-prev]')?.addEventListener('click', () => this.stepVideo(-1));
    this.videoModal.querySelector('[data-pdp-video-next]')?.addEventListener('click', () => this.stepVideo(1));

    // The page's <main> is an isolated stacking context, so a modal inside it can never sit above the fixed site
    // header (it covered the close button on phones). Lift the player to <body> once everything is bound.
    if (this.videoModal.parentElement !== document.body) {
      document.querySelectorAll('body > [data-pdp-video-modal]').forEach((old) => old.remove());
      document.body.appendChild(this.videoModal);
    }

    document.addEventListener('keydown', (event) => {
      if (this.videoModal.hidden) {
        if (event.key === 'Escape' && this.compareModal && !this.compareModal.hidden) this.closeCompareModal();
        return;
      }
      if (event.key === 'Escape') this.closeVideo();
      else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') this.stepVideo(1);
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') this.stepVideo(-1);
      else if (event.key === ' ') { event.preventDefault(); this.toggleVideoPlay(); }
    });
  }

  // Halo-style hover preview: the inline how-to video fades in and plays while
  // the pointer is over the tile, then resets on leave. Clicking still opens the
  // full popup (wired in bindVideoModal). Touch devices skip this and rely on tap.
  bindHowToHover() {
    if (window.matchMedia('(hover: none)').matches) return;
    // With looping thumbnails (bindHowToLoop) the clips already play, so hover has nothing to add.
    if (this.root.dataset.pdpHowToLoop === 'true') return;

    this.videoTriggers.forEach((trigger) => {
      const video = trigger.querySelector('[data-pdp-how-to-video]');
      if (!video) return;

      trigger.addEventListener('mouseenter', () => {
        trigger.classList.add('is-playing');
        try {
          video.currentTime = 0;
          const played = video.play();
          if (played && played.catch) played.catch(() => {});
        } catch (error) {
          /* autoplay may be blocked — safe to ignore */
        }
      });

      trigger.addEventListener('mouseleave', () => {
        trigger.classList.remove('is-playing');
        try {
          video.pause();
        } catch (error) {
          /* no-op */
        }
      });
    });
  }

  toggleVideoPlay() {
    const video = this.videoMedia.querySelector('video');
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
    this.videoModal.classList.toggle('is-paused', video.paused);
  }

  // Looping How to use thumbnails (section setting how_to_loop): while the row is on screen every
  // thumbnail plays a short silent loop, to invite a tap; off screen they pause. A [data-preview-clip]
  // video is already a short clip; a full video is held to its first few seconds instead. Skipped for
  // reduced motion, where the posters stay as they are.
  bindHowToLoop() {
    if (this.root.dataset.pdpHowToLoop !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const track = this.root.querySelector('[data-pdp-how-to-track]');
    if (!track) return;
    const LOOP_SECONDS = 3.5;
    const items = this.videoTriggers
      .map((trigger) => ({ trigger, video: trigger.querySelector('[data-pdp-how-to-video]') }))
      .filter((item) => item.video);
    if (!items.length) return;

    items.forEach(({ video }) => {
      video.muted = true;
      if (!video.hasAttribute('data-preview-clip')) {
        video.addEventListener('timeupdate', () => {
          if (video.currentTime > LOOP_SECONDS) video.currentTime = 0.1;
        });
      }
    });

    const play = () => items.forEach(({ trigger, video }) => {
      video.preload = 'auto';
      const played = video.play();
      const show = () => trigger.classList.add('is-playing');
      if (played && played.then) played.then(show).catch(() => {});
      else show();
    });
    const pause = () => items.forEach(({ video }) => { try { video.pause(); } catch (error) { /* no-op */ } });

    if (!('IntersectionObserver' in window)) { play(); return; }
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? play() : pause()));
    }, { rootMargin: '150px 0px' }).observe(track);
  }

  stepVideo(direction) {
    const total = this.videoTriggers.length;
    if (total < 2) return;
    const next = (this.currentVideoIndex + direction + total) % total;
    this.openVideo(next);
  }

  openVideo(index = 0) {
    const trigger = this.videoTriggers[index];
    if (!trigger) return;

    const src = trigger.dataset.videoSrc;
    if (!src) return;

    this.currentVideoIndex = index;
    this.videoModal.classList.remove('is-paused');
    this.videoModal.classList.remove('is-unmuted');
    this.videoModal.classList.remove('is-ready');
    this.videoMedia.innerHTML = '';

    // Shopify-hosted only. Muted + playsInline so autoplay is allowed inline on
    // mobile; the mute control lets the visitor add sound.
    // With data-pdp-video-sound (section setting video_sound) the clip starts WITH sound: opening it is
    // a tap, which browsers accept as permission. If a browser still refuses (an auto-advanced clip on
    // some phones), the play() failure below falls back to muted rather than leaving the clip stopped.
    const withSound = this.root.dataset.pdpVideoSound === 'true' && !this.videoSoundOff;
    const video = document.createElement('video');
    video.src = src;
    video.playsInline = true;
    video.muted = !withSound;
    if (withSound) this.videoModal.classList.add('is-unmuted');
    video.loop = this.videoTriggers.length < 2;
    this.videoMedia.appendChild(video);
    if (this.videoTriggers.length > 1) {
      video.addEventListener('ended', () => this.stepVideo(1), { once: true });
    }

    // Reveal the video + product card together only once the first frame is
    // decoded, then start playback (playing while opacity:0 would let Chrome
    // pause silent video-only clips to save power). A timeout is a safety net.
    const reveal = () => {
      if (this.videoModal.classList.contains('is-ready')) return;
      this.videoModal.classList.add('is-ready');
      const played = video.play();
      if (played && played.catch) played.catch(() => {
        if (video.muted) return;
        video.muted = true;
        this.videoModal.classList.remove('is-unmuted');
        const retry = video.play();
        if (retry && retry.catch) retry.catch(() => {});
      });
    };
    if (video.readyState >= 2) {
      reveal();
    } else {
      video.addEventListener('loadeddata', reveal, { once: true });
      setTimeout(reveal, 2500);
    }

    const progress = this.videoModal.querySelector('[data-pdp-video-progress]');
    const total = this.videoTriggers.length;
    if (progress && total > 0) {
      progress.hidden = false;
      progress.innerHTML = '';
      for (let i = 0; i < total; i += 1) {
        const bar = document.createElement('button');
        bar.type = 'button';
        bar.setAttribute('aria-label', `Go to video ${i + 1}`);
        bar.appendChild(document.createElement('i'));
        if (i === index) bar.classList.add('is-active');
        if (i < index) bar.classList.add('is-done');
        bar.addEventListener('click', (e) => { e.stopPropagation(); this.openVideo(i); });
        progress.appendChild(bar);
      }
    } else if (progress) {
      progress.hidden = true;
    }

    // The active bar fills as the clip plays, like Instagram's.
    cancelAnimationFrame(this.videoProgressFrame);
    const activeFill = progress && progress.querySelector('button.is-active i');
    if (activeFill) {
      const tick = () => {
        if (video.duration) activeFill.style.width = `${Math.min(100, (video.currentTime / video.duration) * 100)}%`;
        this.videoProgressFrame = requestAnimationFrame(tick);
      };
      tick();
    }
    const nav = this.videoModal.querySelector('[data-pdp-video-nav]');
    if (nav) nav.hidden = total < 2;

    if (this.videoProductCard) this.videoProductCard.hidden = false;
    this.videoModal.hidden = false;
    this.syncBodyScrollLock();
  }

  closeVideo() {
    if (!this.videoModal) return;
    cancelAnimationFrame(this.videoProgressFrame);
    this.videoModal.hidden = true;
    if (this.videoMedia) this.videoMedia.innerHTML = '';
    this.syncBodyScrollLock();
  }

  bindCompareModal() {
    this.root.querySelectorAll('[data-pdp-compare-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openCompareModal());
    });

    this.compareModal?.querySelectorAll('[data-pdp-compare-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeCompareModal());
    });

    this.bindCompareCarousel();
  }

  bindCompareCarousel() {
    if (!this.compareModal) return;
    const track = this.compareModal.querySelector('[data-pdp-compare-track]');
    const prev = this.compareModal.querySelector('[data-pdp-compare-prev]');
    const next = this.compareModal.querySelector('[data-pdp-compare-next]');
    const progress = this.compareModal.querySelector('[data-pdp-compare-progress]');
    if (!track) return;

    const step = () => {
      const card = track.querySelector('.pdp-compare-modal__card');
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '16') || 16;
      return card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.5;
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      if (progress) {
        const visible = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
        const travelled = max > 0 ? track.scrollLeft / max : 0;
        const fill = Math.min(1, Math.max(visible, 0.15));
        progress.style.width = `${fill * 100}%`;
        progress.style.left = `${travelled * (100 - fill * 100)}%`;
      }
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max - 1;
    };

    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    this.updateCompareCarousel = update;
  }

  openCompareModal() {
    if (!this.compareModal) return;
    this.compareModal.hidden = false;
    this.syncBodyScrollLock();
    this.compareModal.querySelector('.pdp-compare-modal__close')?.focus();
    window.requestAnimationFrame(() => this.updateCompareCarousel?.());
  }

  closeCompareModal() {
    if (!this.compareModal) return;
    this.compareModal.hidden = true;
    this.syncBodyScrollLock();
  }

  /**
   * Reviews pop-up: the Trustpilot badge at the top of the buy box opens a sheet
   * holding Trustpilot's List widget. The widget is only loaded on first open.
   */
  bindReviewsModal() {
    if (!this.reviewsModal) return;
    this.root.querySelectorAll('[data-pdp-reviews-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openReviewsModal(btn));
    });
    this.reviewsModal.querySelectorAll('[data-pdp-reviews-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeReviewsModal());
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.reviewsModal.hidden) this.closeReviewsModal();
    });
  }

  openReviewsModal(opener) {
    if (!this.reviewsModal) return;
    this.reviewsOpener = opener || null;
    this.reviewsModal.hidden = false;
    this.syncBodyScrollLock();
    this.loadReviewsWidget();
    // Focus the sheet, not the close button, so no focus ring shows on tap.
    this.reviewsModal.querySelector('.pdp-compare-modal__dialog')?.focus();
  }

  closeReviewsModal() {
    if (!this.reviewsModal) return;
    this.reviewsModal.hidden = true;
    this.syncBodyScrollLock();
    this.reviewsOpener?.focus();
  }

  // The Trustpilot bootstrap is deferred, so wait briefly for it before giving up
  // (the widget's fallback link to Trustpilot stays visible in that case).
  // Only the layout for the current width is loaded: the Grid from 750px, the
  // List below. Each open re-checks, so turning a tablet loads the other once.
  loadReviewsWidget(tries = 20) {
    const layout = window.matchMedia('(min-width: 750px)').matches ? 'grid' : 'list';
    const widget = this.reviewsModal?.querySelector(`[data-pdp-reviews-widget="${layout}"]`);
    if (!widget || widget.dataset.loaded === 'true') return;
    if (!window.Trustpilot || !window.Trustpilot.loadFromElement) {
      if (tries > 0) window.setTimeout(() => this.loadReviewsWidget(tries - 1), 250);
      else this.reviewsModal.querySelector('[data-pdp-reviews-loading]')?.setAttribute('hidden', '');
      return;
    }
    widget.classList.add('trustpilot-widget');
    window.Trustpilot.loadFromElement(widget, true);
    widget.dataset.loaded = 'true';
    this.hideReviewsLoadingWhenReady(widget);
  }

  // The list draws itself a moment after its frame loads, so the "Loading
  // reviews…" line underneath is hidden shortly after the frame's load event.
  hideReviewsLoadingWhenReady(widget, tries = 40) {
    const loading = this.reviewsModal?.querySelector('[data-pdp-reviews-loading]');
    if (!loading) return;
    const frame = widget.querySelector('iframe');
    if (!frame) {
      if (tries > 0) window.setTimeout(() => this.hideReviewsLoadingWhenReady(widget, tries - 1), 100);
      else loading.hidden = true;
      return;
    }
    frame.addEventListener('load', () => window.setTimeout(() => { loading.hidden = true; }, 800), { once: true });
    window.setTimeout(() => { loading.hidden = true; }, 10000);
  }

  /**
   * Desktop gallery pane: a pinned viewport-tall pane that scrolls inside
   * itself (the info column scrolls with the page). Hand-off to the page at
   * either end is the browser's own scroll chaining; a wheel handler that
   * re-routed it as window.scrollBy jumps was the source of the jerky feel.
   * Gallery rail + arrows track the pane's own scrollTop.
   */
  bindGalleryPaneScroll() {
    const galleryCol = this.root.querySelector('[data-pdp-gallery-col]');
    if (!galleryCol) return;

    const desktopMq = window.matchMedia('(min-width: 750px)');
    const galleryRail = this.root.querySelector('[data-pdp-gallery-scroll]');
    const gallerySticky = this.root.querySelector('[data-pdp-gallery-scroll-sticky]');
    const galleryActive = this.root.querySelector('[data-pdp-gallery-scroll-active]');
    const galleryRest = this.root.querySelector('[data-pdp-gallery-scroll-rest]');
    const galleryLine = this.root.querySelector('[data-pdp-gallery-scroll-line]');
    const galleryUp = this.root.querySelector('[data-pdp-gallery-scroll-up]');
    const galleryDown = this.root.querySelector('[data-pdp-gallery-scroll-down]');

    let galleryRailHideTimer;

    const setGalleryRailVisible = (visible) => {
      if (!galleryCol.classList.contains('has-gallery-scroll')) return;
      galleryCol.classList.toggle('is-gallery-rail-visible', visible);
    };

    const pulseGalleryRail = () => {
      setGalleryRailVisible(true);
      window.clearTimeout(galleryRailHideTimer);
      galleryRailHideTimer = window.setTimeout(() => {
        if (!galleryCol.matches(':hover')) setGalleryRailVisible(false);
      }, 1500);
    };

    // Longer pin on big screens (Justyn 14/10/2026). The buy box can be barely taller than the
    // screen, so the pinned gallery let go after a few pixels and the images under the first
    // row were never seen. The gallery column now runs one pane plus however far it takes to
    // reach the load-more button, and the info column holds still (or holds once its end is in view) for
    // that stretch, while page scrolling moves the gallery (see the scroll listener below).
    // Not every image: the rest stay behind the load-more button.
    const galleryWrap = galleryCol.parentElement;
    const infoPane = this.root.querySelector('[data-pdp-info-col]');
    let frozenGalleryReveal = null;
    let lastGalleryReveal = 0;
    this.root.querySelector('[data-pdp-gallery-load-more]')?.addEventListener('click', () => {
      if (frozenGalleryReveal === null) frozenGalleryReveal = lastGalleryReveal;
    }, { capture: true });
    const layoutGalleryPin = () => {
      if (!galleryWrap || !infoPane) return;
      if (!desktopMq.matches) {
        galleryWrap.style.minHeight = '';
        infoPane.style.position = '';
        infoPane.style.top = '';
        return;
      }
      const zoom = galleryWrap.getBoundingClientRect().height / (galleryWrap.offsetHeight || 1) || 1;
      const paneHeight = galleryCol.clientHeight;
      // Long enough to reach the "Load more images" button at the foot of the first images
      // (Justyn 14/10/2026). Frozen once that button is clicked, so revealing the rest doesn't
      // stretch the page under the shopper.
      const reveal = frozenGalleryReveal ?? Math.max(0, galleryCol.scrollHeight - paneHeight);
      lastGalleryReveal = reveal;
      const minHeight = `${Math.ceil(paneHeight + reveal)}px`;
      if (galleryWrap.style.minHeight !== minHeight) galleryWrap.style.minHeight = minHeight;
      const pinTop = parseFloat(window.getComputedStyle(galleryCol).top) || 0;
      const top = `${Math.round(Math.min(pinTop, window.innerHeight / zoom - infoPane.offsetHeight))}px`;
      infoPane.style.position = 'sticky';
      if (infoPane.style.top !== top) infoPane.style.top = top;
    };

    const updateGalleryRail = () => {
      layoutGalleryPin();
      if (!galleryRail || !galleryActive || !galleryRest || !galleryLine) return;

      if (!desktopMq.matches) {
        galleryCol.classList.remove('has-gallery-scroll', 'is-gallery-rail-visible');
        galleryRail.hidden = true;
        if (gallerySticky) gallerySticky.hidden = true;
        return;
      }

      const maxScroll = galleryCol.scrollHeight - galleryCol.clientHeight;
      if (maxScroll <= 4) {
        galleryCol.classList.remove('has-gallery-scroll', 'is-gallery-rail-visible');
        galleryRail.hidden = true;
        if (gallerySticky) gallerySticky.hidden = true;
        return;
      }

      galleryCol.classList.add('has-gallery-scroll');
      galleryRail.hidden = false;
      if (gallerySticky) gallerySticky.hidden = false;

      const progress = galleryCol.scrollTop / maxScroll;
      const lineStyles = window.getComputedStyle(galleryLine);
      const linePad =
        (parseFloat(lineStyles.paddingTop) || 0) + (parseFloat(lineStyles.paddingBottom) || 0);
      const lineHeight = Math.max(galleryLine.clientHeight - linePad, 2);
      const activeHeight = Math.max(progress * lineHeight, 2);
      galleryActive.style.height = `${activeHeight}px`;
      galleryRest.style.height = `${Math.max(lineHeight - activeHeight, 2)}px`;

      if (galleryUp) galleryUp.disabled = galleryCol.scrollTop <= 1;
      if (galleryDown) galleryDown.disabled = galleryCol.scrollTop >= maxScroll - 1;
    };

    galleryCol.addEventListener('mouseenter', () => setGalleryRailVisible(true));
    galleryCol.addEventListener('mouseleave', () => setGalleryRailVisible(false));
    galleryCol.addEventListener('scroll', () => {
      updateGalleryRail();
      pulseGalleryRail();
    }, { passive: true });

    galleryUp?.addEventListener('click', () => {
      galleryCol.scrollBy({ top: -galleryCol.clientHeight * 0.75, behavior: 'smooth' });
      pulseGalleryRail();
    });
    galleryDown?.addEventListener('click', () => {
      galleryCol.scrollBy({ top: galleryCol.clientHeight * 0.75, behavior: 'smooth' });
      pulseGalleryRail();
    });

    // While the gallery is pinned, scrolling the page also moves the gallery by the same amount,
    // so more images come into view as you read down the buy box (Justyn 14/10/2026). It adds
    // the page's movement rather than setting a position, so a spot the shopper scrolled the
    // gallery to by hand is kept. The gallery stops at its end; anything past that is still
    // there through the scroll control.
    let lastPageY = window.scrollY;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      const delta = y - lastPageY;
      lastPageY = y;
      if (!delta || !desktopMq.matches || !galleryWrap) return;
      if (!galleryCol.classList.contains('has-gallery-scroll')) return;
      const wrap = galleryWrap.getBoundingClientRect();
      const pane = galleryCol.getBoundingClientRect();
      // Pinned: the column has started to scroll past the pane but not run out underneath it.
      if (wrap.top >= pane.top - 1 || wrap.bottom <= pane.bottom + 1) return;
      galleryCol.scrollTop += delta;
    }, { passive: true });

    window.addEventListener('resize', updateGalleryRail);
    desktopMq.addEventListener('change', updateGalleryRail);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateGalleryRail);
      observer.observe(galleryCol);
      const infoCol = this.root.querySelector('[data-pdp-info-col]');
      if (infoCol) observer.observe(infoCol);
    }

    this.root.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', updateGalleryRail, { once: true });
    });
    if (document.fonts?.ready) document.fonts.ready.then(updateGalleryRail);

    updateGalleryRail();
    window.requestAnimationFrame(updateGalleryRail);
  }

  bindSizeChartModal() {
    const modal = this.root.querySelector('[data-pdp-size-chart-modal]');
    if (!modal) return;

    // Delegated on the document, NOT scoped to this.root.
    //
    // The modal lives inside the buy-box section, but the size-chart trigger does not have to:
    // comp-s3 renders its own "Size Chart" link next to its mobile size picker, and a
    // root-scoped querySelectorAll never sees it, so that link was inert on every click. It was
    // reported as "the second size chart doesn't do anything".
    //
    // Delegation also survives a section re-render from the theme editor, which the direct
    // per-element binding above did not.
    //
    // Guarded with a flag on the document so several PdpRedesign instances on one page cannot
    // stack listeners and open the modal twice per click.
    if (!document.__pdpSizeChartOpenBound) {
      document.__pdpSizeChartOpenBound = true;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-pdp-size-chart-open]');
        if (!trigger) return;
        // An <a href="#"> trigger would otherwise jump to the top of the page.
        event.preventDefault();
        this.openSizeChartModal();
      });
    }

    modal.querySelectorAll('[data-pdp-size-chart-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeSizeChartModal());
    });

    modal.querySelector('[data-pdp-size-chart-video-open]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.openSizeChartVideo();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isModalOpen(modal)) this.closeSizeChartModal();
    });

    this.renderSizeChartTable(modal);
    this.bindSizeFinder(modal);
  }

  /**
   * Size finder (credibility buy box, snippets/pdp-size-finder.liquid). Reads the product's size
   * chart JSON, matches the typed inseam (and upper thigh) to a size, and "Select <size>" ticks
   * that size in the variant picker. On an inseam boundary the bigger size wins (Justyn
   * 14/10/2026: "always the bigger one"); a thigh too big for that size moves it up.
   */
  bindSizeFinder(modal) {
    const box = modal.querySelector('[data-pdp-sf]');
    if (!box) return;
    let data;
    try {
      data = JSON.parse(box.getAttribute('data-chart'));
    } catch (error) {
      return;
    }
    if (!data || !Array.isArray(data.sizes) || !data.sizes.length) return;

    const units = ['cms', 'inches'].filter((u) => data[u] && data[u].inseam);
    if (!units.length) return;
    let unit = units[0];
    const hasThigh = units.some((u) => Array.isArray(data[u].upper_thigh));
    if (!hasThigh) box.querySelectorAll('[data-pdp-sf-thigh-only]').forEach((node) => node.remove());
    if (units.length < 2) box.querySelector('.pdp-sf__units')?.remove();

    const nums = (text) => (String(text || '').match(/\d+(?:\.\d+)?/g) || []).map(Number);
    const chart = (u) => ({
      inseam: (data[u].inseam || []).map((text) => { const n = nums(text); return [n[0], n.length > 1 ? n[1] : n[0]]; }),
      thigh: (data[u].upper_thigh || []).map((text) => nums(text).pop()),
      inseamText: data[u].inseam || [],
      thighText: data[u].upper_thigh || [],
    });
    const label = (text, u) => {
      let out = String(text || '').replace(/\s*-\s*/g, '-').replace(/^Up to/i, 'up to');
      if (u === 'cms' && !/cm/i.test(out)) out += ' cm';
      return out;
    };

    const inseamInput = box.querySelector('[data-pdp-sf-input="inseam"]');
    const thighInput = box.querySelector('[data-pdp-sf-input="upper_thigh"]');
    const result = box.querySelector('[data-pdp-sf-result]');
    const sizeEl = box.querySelector('[data-pdp-sf-size]');
    const detailEl = box.querySelector('[data-pdp-sf-detail]');
    const flagEl = box.querySelector('[data-pdp-sf-flag]');
    const selectBtn = box.querySelector('[data-pdp-sf-select]');
    const tableEl = box.querySelector('[data-pdp-sf-table]');
    const read = (input) => {
      if (!input) return null;
      const value = parseFloat(String(input.value).replace(',', '.'));
      return Number.isFinite(value) && value > 0 ? value : null;
    };
    let picked = -1;

    const renderTable = () => {
      if (!tableEl) return;
      const c = chart(unit);
      const esc = (value) => String(value == null ? '' : value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      let html = '<table class="pdp-sf__tbl"><thead><tr><th>Size</th><th>Inseam</th>';
      if (hasThigh) html += '<th>Upper thigh</th>';
      html += '</tr></thead><tbody>';
      data.sizes.forEach((size, i) => {
        html += `<tr${i === picked ? ' class="is-picked"' : ''}><td>${esc(size)}</td><td>${esc(label(c.inseamText[i], unit))}</td>`;
        if (hasThigh) html += `<td>${esc(label(c.thighText[i], unit))}</td>`;
        html += '</tr>';
      });
      tableEl.innerHTML = `${html}</tbody></table>`;
    };

    const compute = () => {
      const c = chart(unit);
      const inseam = read(inseamInput);
      const thigh = read(thighInput);
      let idx = -1;
      let flag = '';
      if (inseam != null && c.inseam.length) {
        idx = c.inseam.findLastIndex(([lo, hi]) => inseam >= lo && inseam <= hi);
        if (idx === -1) {
          idx = inseam < c.inseam[0][0] ? 0 : c.inseam.length - 1;
          flag = 'Your inseam is outside our size range, so this is the closest fit.';
        }
      }
      if (thigh != null && c.thigh.length) {
        let t = c.thigh.findIndex((max) => thigh <= max);
        if (t === -1) {
          t = c.thigh.length - 1;
          flag = flag || 'Your upper thigh is above our largest size, so this is the closest fit.';
        }
        if (t > idx) {
          if (idx !== -1 && !flag) flag = 'Sized up to fit your upper thigh.';
          idx = t;
        }
      }
      picked = idx;
      if (idx === -1) {
        result.hidden = true;
      } else {
        sizeEl.textContent = data.sizes[idx];
        const parts = [];
        if (c.inseamText[idx]) parts.push(`Inseam ${label(c.inseamText[idx], unit)}`);
        if (c.thighText[idx]) parts.push(`Upper thigh ${label(c.thighText[idx], unit)}`);
        detailEl.textContent = parts.join(' · ');
        flagEl.textContent = flag;
        flagEl.hidden = !flag;
        selectBtn.textContent = `Select ${data.sizes[idx]}`;
        result.hidden = false;
      }
      renderTable();
    };

    box.querySelectorAll('[data-pdp-sf-unit]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const next = btn.getAttribute('data-pdp-sf-unit');
        if (next === unit || !data[next]) return;
        const factor = next === 'inches' ? 1 / 2.54 : 2.54;
        [inseamInput, thighInput].forEach((input) => {
          const value = read(input);
          if (value != null) input.value = String(Math.round(value * factor * 10) / 10);
        });
        unit = next;
        box.querySelectorAll('[data-pdp-sf-unit]').forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', String(on));
        });
        box.querySelectorAll('[data-pdp-sf-suffix]').forEach((s) => { s.textContent = unit === 'cms' ? 'cm' : 'in'; });
        compute();
      });
    });
    [inseamInput, thighInput].forEach((input) => input?.addEventListener('input', compute));

    // Ticks the matching size in the variant picker ("Extra Small" matches "X-Small"), then closes.
    selectBtn?.addEventListener('click', () => {
      if (picked < 0) return;
      const norm = (text) => String(text || '').toLowerCase().replace(/extra[\s-]*/g, 'x').replace(/[^a-z0-9]/g, '');
      const sizeInputs = [...this.root.querySelectorAll('.pdp-variant-picker__option--size input[data-pdp-option-input]')];
      const want = norm(data.sizes[picked]);
      const match = sizeInputs.find((input) => norm(input.value) === want)
        || (sizeInputs.length === data.sizes.length ? sizeInputs[picked] : null);
      if (match && !match.checked) match.click();
      this.closeSizeChartModal();
    });

    renderTable();
  }

  renderSizeChartTable(modal) {
    const wrap = modal.querySelector('[data-pdp-size-chart-table]');
    if (!wrap) return;

    let data;
    try {
      data = JSON.parse(wrap.getAttribute('data-pdp-size-chart-table'));
    } catch (error) {
      return;
    }
    if (!data || !Array.isArray(data.columns) || !Array.isArray(data.sizes)) return;

    const esc = (value) => String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const hasCms = data.cms && typeof data.cms === 'object';
    const hasInches = data.inches && typeof data.inches === 'object';
    let unit = hasCms ? 'cms' : 'inches';

    const buildTable = () => {
      const units = data[unit] || {};
      let html = '<div class="pdp-size-chart-modal__table-inner"><table class="pdp-size-chart-modal__table"><thead><tr>';
      data.columns.forEach((col) => { html += `<th>${esc(col.label)}</th>`; });
      html += '</tr></thead><tbody>';
      data.sizes.forEach((size, rowIndex) => {
        html += '<tr>';
        data.columns.forEach((col) => {
          if (col.key === 'size') {
            html += `<td>${esc(size)}</td>`;
          } else {
            const values = units[col.key] || [];
            html += `<td>${esc(values[rowIndex] || '')}</td>`;
          }
        });
        html += '</tr>';
      });
      return `${html}</tbody></table></div>`;
    };

    const render = () => {
      let toggle = '';
      if (hasCms && hasInches) {
        toggle = '<div class="pdp-size-chart-modal__tabs" role="tablist" aria-label="Units">'
          + `<button type="button" role="tab" data-unit="cms" class="pdp-size-chart-modal__tab${unit === 'cms' ? ' is-active' : ''}" aria-selected="${unit === 'cms'}">CMS</button>`
          + `<button type="button" role="tab" data-unit="inches" class="pdp-size-chart-modal__tab${unit === 'inches' ? ' is-active' : ''}" aria-selected="${unit === 'inches'}">INCHES</button>`
          + '</div>';
      } else if (hasCms || hasInches) {
        const label = hasCms ? 'CMS' : 'INCHES';
        toggle = `<div class="pdp-size-chart-modal__tabs" role="tablist" aria-label="Units"><button type="button" role="tab" class="pdp-size-chart-modal__tab is-active" aria-selected="true">${label}</button></div>`;
      }
      wrap.innerHTML = toggle + buildTable();
      wrap.querySelectorAll('[data-unit]').forEach((btn) => {
        btn.addEventListener('click', () => {
          unit = btn.getAttribute('data-unit');
          render();
        });
      });
    };

    render();
  }

  openSizeChartModal() {
    const modal = this.getSizeChartModal();
    if (!modal) return;

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    this.sizeChartModal = modal;

    modal.hidden = false;
    modal.classList.remove('is-video');
    const video = modal.querySelector('[data-pdp-size-chart-video]');
    if (video) video.hidden = true;
    this.syncBodyScrollLock();
    modal.querySelector('.pdp-size-chart-modal__close')?.focus();
  }

  openSizeChartVideo() {
    const modal = this.getSizeChartModal();
    if (!modal) return;
    const video = modal.querySelector('[data-pdp-size-chart-video]');
    if (!video) return;
    modal.classList.add('is-video');
    video.hidden = false;
  }

  closeSizeChartModal() {
    const modal = this.getSizeChartModal();
    if (!modal) return;
    modal.hidden = true;
    modal.classList.remove('is-video');
    const video = modal.querySelector('[data-pdp-size-chart-video]');
    if (video) {
      video.hidden = true;
      video.querySelectorAll('video').forEach((node) => node.pause());
      video.querySelectorAll('iframe').forEach((iframe) => {
        const src = iframe.src;
        iframe.src = src;
      });
    }
    this.syncBodyScrollLock();
  }

  bindBedsheetQuiz() {
    const triggers = this.root.querySelectorAll('[data-pdp-bedsheet-quiz-open]');
    if (!triggers.length) return;

    triggers.forEach((btn) => {
      if (btn.dataset.pdpBedsheetQuizBound === 'true') return;
      btn.dataset.pdpBedsheetQuizBound = 'true';
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.openBedsheetQuiz();
      });
    });

    if (PdpRedesign.bedsheetQuizGlobalBound) return;
    PdpRedesign.bedsheetQuizGlobalBound = true;

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.quiz-popup-close-icon, [data-close-icon="close-icon"], [data-pdp-bedsheet-quiz-close]')) {
        return;
      }
      PdpRedesign.closeBedsheetQuizOverlay();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      PdpRedesign.closeBedsheetQuizOverlay();
    });
  }

  openBedsheetQuiz() {
    const overlay = this.getBedsheetQuizOverlay();
    if (!overlay) return;

    if (overlay.hasAttribute('hidden')) overlay.hidden = false;
    overlay.classList.add('is-open');
    overlay.style.removeProperty('display');

    this.syncBodyScrollLock();
  }

  closeBedsheetQuiz() {
    PdpRedesign.closeBedsheetQuizOverlay();
    this.syncBodyScrollLock();
  }

  static closeBedsheetQuizOverlay() {
    const overlay = document.querySelector('.shopify-section.section-bedsheet-quiz')
      || document.querySelector('[data-pdp-bedsheet-quiz-modal]');
    if (!overlay) return;

    overlay.classList.remove('is-open');
    overlay.style.removeProperty('display');
    if (overlay.hasAttribute('data-pdp-bedsheet-quiz-modal')) overlay.hidden = true;
    document.body.style.overflow = '';
  }

  bindStickyAtcScroll() {
    if (PdpRedesign.stickyAtcScrollBound) return;
    PdpRedesign.stickyAtcScrollBound = true;

    if (!PdpRedesign.nativeScrollIntoView) {
      PdpRedesign.nativeScrollIntoView = Element.prototype.scrollIntoView;
    }
    const nativeScrollIntoView = PdpRedesign.nativeScrollIntoView;

    const scrollToBuy = () => {
      const redesign = document.querySelector('.pdp-redesign-wrapper');
      const target = document.getElementById('buy')
        || (redesign && redesign.querySelector('[data-pdp-variant-picker]'))
        || (redesign && redesign.querySelector('.pdp-redesign__info'))
        || redesign;
      if (!target) return;
      const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - 12);
      window.scrollTo(0, top);
    };

    Element.prototype.scrollIntoView = function (...args) {
      const redesign = document.querySelector('.pdp-redesign-wrapper');
      if (redesign) {
        const isHiddenProduct = this.id === 'Product'
          || (typeof this.closest === 'function' && this.closest('#Product'));
        if (isHiddenProduct) {
          scrollToBuy();
          return;
        }
      }
      return nativeScrollIntoView.apply(this, args);
    };

    const onUpdateIntent = (event) => {
      if (!document.querySelector('.pdp-redesign-wrapper')) return;

      const link = event.target.closest('[role="link"]');
      if (!link) return;

      const bar = link.closest('[data-replo-product-container]');
      if (!bar || !bar.querySelector('[data-replo-add-product-variant-to-cart]')) return;
      if (!(link.textContent || '').toLowerCase().includes('update')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      scrollToBuy();
    };

    document.addEventListener('click', onUpdateIntent, true);
    document.addEventListener('pointerdown', onUpdateIntent, true);
  }
  /**
   * Desktop: the sections after the buy box fade up the first time they
   * scroll into view (Gymshark's PDP does this: 12px rise, 0.8s, once).
   * Only sections still below the fold at load are touched, so nothing on
   * screen ever blinks out. Skipped: thin wrappers (the fixed sticky ATC
   * sits in a zero-height section, and opacity on it would hide the bar),
   * and sections that already animate their own content (AOS, the
   * homepage's data-reveal), so nothing animates twice. The rise runs
   * through the Web Animations API and leaves no transform behind, since
   * a lingering transform would trap any position:fixed child.
   */
  bindSectionReveal() {
    if (PdpRedesign.sectionRevealBound) return;
    PdpRedesign.sectionRevealBound = true;

    if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const pdpSection = this.root.closest('.shopify-section');
    if (!pdpSection) return;

    const viewportHeight = window.innerHeight;
    const targets = [];
    for (let node = pdpSection.nextElementSibling; node; node = node.nextElementSibling) {
      if (!node.classList.contains('shopify-section')) continue;
      // Template sections only; the footer group renders as a sibling too.
      if (!node.id.startsWith('shopify-section-template--')) continue;
      if (node.offsetHeight < 80) continue;
      if (node.getBoundingClientRect().top < viewportHeight) continue;
      if (node.querySelector('[data-aos], [data-reveal], [data-reveal-group], .pdp-sticky')) continue;
      const position = window.getComputedStyle(node).position;
      if (position === 'fixed' || position === 'sticky') continue;
      targets.push(node);
    }
    if (!targets.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const node = entry.target;
        if (!entry.isIntersecting) {
          // A jump (End key, an anchor link) can carry a section from below
          // the fold to above it between frames, so it never intersects.
          // Show it without the animation rather than leave it invisible.
          if (entry.boundingClientRect.top < 0) {
            observer.unobserve(node);
            node.classList.remove('pdp-reveal-pending');
          }
          return;
        }
        observer.unobserve(node);
        node.animate(
          [
            { opacity: 0, transform: 'translateY(12px)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 800, easing: 'cubic-bezier(0.01, 0.02, 0.29, 1)' },
        );
        node.classList.remove('pdp-reveal-pending');
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });

    targets.forEach((node) => {
      node.classList.add('pdp-reveal-pending');
      observer.observe(node);
    });
  }
}

document.querySelectorAll('[data-pdp-redesign]').forEach((root) => {
  if (root.dataset.updateUrl === undefined) {
    root.dataset.updateUrl = 'true';
  }
  new PdpRedesign(root);
});

// 20/10/2026 (Justyn: "make the image here so it's not cut off but fades out"): the add-on slider's peeking card
// fades out (pdp-redesign.css); once the slider is scrolled to its last card the fade comes off.
(function () {
  function sync(list) {
    list.classList.toggle('is-at-end', list.scrollLeft + list.clientWidth >= list.scrollWidth - 2);
  }
  function syncAll() {
    document.querySelectorAll('.pdp-addrow__list--slider').forEach(function (list) {
      if (!list.__fadeBound) {
        list.__fadeBound = true;
        list.addEventListener('scroll', function () { sync(list); }, { passive: true });
      }
      sync(list);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncAll);
  else syncAll();
  window.addEventListener('load', syncAll);
  window.addEventListener('resize', syncAll);
})();
